import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePrompts } from '@/hooks/usePrompts';
import { useNavigation } from '@/contexts/NavigationContext';
import { useScopeOptions } from '@/hooks/useScopeOptions';
import { Search, Eye, EyeOff, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { getProjectIdFromFilter } from '@/types/navigation';

interface PromptSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (prompt: { id: string; name: string; content: string }) => void;
  activeConversationId?: string | null;
}

export const PromptSelectorModal = ({ open, onOpenChange, onSelect, activeConversationId }: PromptSelectorModalProps) => {
  const { navigation, scopeFilter, setScopeFilter, navigateToTab, navigateToProject, navigateToPersonal, navigateToGlobal } = useNavigation();
  
  const scopeOptions = useScopeOptions();

  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleOpenChange = (val: boolean) => {
    if (val) {
      setSearch('');
      setExpandedId(null);
      setCopiedId(null);
    }
    onOpenChange(val);
  };

  const projectId = navigation.projectId;
  const { data: globalPrompts = [] } = usePrompts('global', undefined, projectId);
  const { data: personalPrompts = [] } = usePrompts('personal');

  const allPrompts = useMemo(() => {
    const map = new Map<string, typeof globalPrompts[0]>();
    globalPrompts.forEach(p => map.set(p.id, p));
    personalPrompts.forEach(p => map.set(p.id, p));
    return Array.from(map.values());
  }, [globalPrompts, personalPrompts]);

  const filtered = useMemo(() => {
    let list = allPrompts;
    const filterProjectId = getProjectIdFromFilter(scopeFilter);

    if (filterProjectId) {
      list = list.filter(p => p.project_id === filterProjectId);
    } else if (scopeFilter === 'global') {
      list = list.filter(p => p.scope_type === 'global' && !p.project_id);
    } else if (scopeFilter === 'personal') {
      list = list.filter(p => p.scope_type === 'personal' && !p.project_id);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allPrompts, scopeFilter, search]);

  const handleCopy = async (prompt: { id: string; name: string; content: string }) => {
    await navigator.clipboard.writeText(prompt.content);
    setCopiedId(prompt.id);
    setTimeout(() => setCopiedId(null), 2000);

    toast.success('Prompt copiado!');

    if (!activeConversationId) {
      window.dispatchEvent(new CustomEvent('inject-prompt', { detail: prompt.content }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>Selecionar Prompt</DialogTitle>
            <button
              onClick={() => {
                const filterProjectId = getProjectIdFromFilter(scopeFilter);
                if (filterProjectId) {
                  navigateToProject(filterProjectId);
                } else if (scopeFilter === 'personal') {
                  navigateToPersonal();
                } else {
                  navigateToGlobal();
                }
                navigateToTab('prompts');
                onOpenChange(false);
              }}
              className="flex items-center gap-1 text-xs text-tertiary hover:text-primary transition-colors"
              title="Abrir biblioteca de prompts"
            >
              <ExternalLink className="w-3 h-3" />
              Ver biblioteca
            </button>
          </div>
        </DialogHeader>

        {/* Scope filter tags */}
        <div className="flex items-center gap-2 flex-nowrap">
          {scopeOptions.map(s => (
            <button
              key={s.id}
              onClick={() => setScopeFilter(s.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 whitespace-nowrap ${
                scopeFilter === s.id
                  ? 'bg-brand-primary text-white border-transparent'
                  : 'border-color text-secondary hover:bg-hover'
              }`}
            >
              {s.icon}{s.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
          <input
            type="text"
            placeholder="Buscar prompts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-tertiary text-center py-8">Nenhum prompt encontrado</p>
          ) : (
            filtered.map(prompt => (
              <div key={prompt.id} className="rounded-md hover:bg-hover transition-colors">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <button
                    onClick={() => {
                      onSelect({ id: prompt.id, name: prompt.name, content: prompt.content });
                      onOpenChange(false);
                    }}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-primary truncate">{prompt.name}</span>
                      {prompt.category && (
                        <span className="text-xs text-tertiary bg-secondary px-2 py-0.5 rounded-full ml-2 shrink-0">
                          {prompt.category}
                        </span>
                      )}
                    </div>
                    {prompt.description && (
                      <p className="text-xs text-tertiary mt-0.5 line-clamp-1">{prompt.description}</p>
                    )}
                  </button>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId(expandedId === prompt.id ? null : prompt.id);
                      }}
                      className="p-1.5 rounded-md text-tertiary hover:text-primary hover:bg-active transition-colors"
                      title="Ver prompt"
                    >
                      {expandedId === prompt.id ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy({ id: prompt.id, name: prompt.name, content: prompt.content });
                      }}
                      className="p-1.5 rounded-md text-tertiary hover:text-primary hover:bg-active transition-colors"
                      title="Copiar prompt"
                    >
                      {copiedId === prompt.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {expandedId === prompt.id && (
                  <div className="px-3 pb-3">
                    <div className="max-h-32 overflow-y-auto rounded-md bg-secondary p-3 text-xs text-secondary font-mono whitespace-pre-wrap">
                      {prompt.content}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
